import React, { useEffect, useState } from 'react';
import './Billing.scss';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatPhoneNo } from '../../../helpers/common';

export interface iFormBillingDetails {
    firstNameBilling: string;
    lastNameBilling: string;
    contactBilling: string;
    emailBilling: string;
  vemailBilling: string;
}
export interface BillingAddessProps {
    userType: string | undefined;
    onInputChange: (fieldName: string, value: any) => void;
    onInputBlur: (fieldName: string, value: any) => void;
    formBillingDetails: iFormBillingDetails;
    validationErrors: Record<string, string>;
    billingUser: any;
    isReadOnly: boolean;
    showCompanyName?: boolean;
    companyName?: string;
    onCompanyNameChange?: (value: string) => void;
    onCompanyNameBlur?: (value: string) => void;
}
const Billing: React.FC<BillingAddessProps> = ({
    userType,
    onInputChange,
    onInputBlur,
    formBillingDetails,
    isReadOnly,
    validationErrors,
    billingUser,
    showCompanyName,
    companyName = '',
    onCompanyNameChange,
    onCompanyNameBlur,
}) => {
    const { t } = useTranslation();
    const handleFieldChange = (fieldName: string, value: any) => {
        onInputChange(fieldName, value); // Notify the parent component about the form change
    };
    const [userData, setUserData] = useState<iFormBillingDetails | undefined>(undefined);
    useEffect(() => {

        if (billingUser) {
            setUserData({
                ...userData,
                firstNameBilling: billingUser.firstName,
                lastNameBilling: billingUser.lastName,
                contactBilling: billingUser.phoneNumber,
                emailBilling: formBillingDetails.emailBilling,
              vemailBilling: ""
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billingUser]);
    return (
       <div className="Billing" role="region" aria-labelledby="billingSectionHeader">
         <h3 id="billingSectionHeader" className="visually-hidden">{t('BILLING_INFORMATION')}</h3>
         <Row className="billing-fields g-2 w-100 mx-0">
         <Col md={6}>
           <Form.Group controlId="firstNameBilling">
             <Form.Label className='visually-hidden' htmlFor="firstNameBilling">{t('FIRST_NAME')}</Form.Label>
             <Form.Control
               id="firstNameBilling"
               required
               aria-required="true"
               aria-invalid={!!validationErrors.firstNameBilling}
               aria-describedby={validationErrors.firstNameBilling ? "firstNameBillingError" : undefined}
               placeholder={t('FIRST_NAME_REQUIRED')}
               type="text"
               autoComplete="none"
               className={`form-control ${isReadOnly ? "disabled" : ""}`}
               name="firstNameBilling"
               readOnly={isReadOnly}
               value={formBillingDetails.firstNameBilling}
               onBlur={(e) => onInputBlur('firstNameBilling', e.target.value)}
               onChange={(e) => handleFieldChange('firstNameBilling', e.target.value)}
               isInvalid={!!validationErrors.firstNameBilling}
             />
             <Form.Control.Feedback type="invalid" id="firstNameBillingError" aria-live="polite">
               {validationErrors.firstNameBilling}
             </Form.Control.Feedback>
           </Form.Group>
         </Col>
         <Col md={6}>
           <Form.Group controlId="lastNameBilling">
             <Form.Label className='visually-hidden' htmlFor="lastNameBilling">{t('LAST_NAME')}</Form.Label>
             <Form.Control
               id="lastNameBilling"
               required
               aria-required="true"
               aria-invalid={!!validationErrors.lastNameBilling}
               aria-describedby={validationErrors.lastNameBilling ? "lastNameBillingError" : undefined}
               placeholder={t('LAST_NAME_REQUIRED')}
               type="text"
               autoComplete="none"
               className={`form-control ${isReadOnly ? "disabled" : ""}`}
               name="lastNameBilling"
               readOnly={isReadOnly}
               value={formBillingDetails.lastNameBilling}
               onBlur={(e) => onInputBlur('lastNameBilling', e.target.value)}
               onChange={(e) => handleFieldChange('lastNameBilling', e.target.value)}
               isInvalid={!!validationErrors.lastNameBilling}
             />
             <Form.Control.Feedback type="invalid" id="lastNameBillingError" aria-live="polite">
               {validationErrors.lastNameBilling}
             </Form.Control.Feedback>
           </Form.Group>
         </Col>
         {showCompanyName && onCompanyNameChange && (
           <Col md={6} className="company-name-field">
             <Form.Group controlId="companyName" className="mb-0">
               <Form.Label className='visually-hidden' htmlFor="companyName">{t('COMPANY_NAME')}</Form.Label>
               <Form.Control
                 id="companyName"
                 aria-invalid={!!validationErrors.companyName}
                 aria-describedby={validationErrors.companyName ? "companyNameError" : undefined}
                 placeholder={t('COMPANY_NAME')}
                 type="text"
                 autoComplete="organization"
                 className={`form-control ${isReadOnly ? "disabled" : ""}`}
                 name="companyName"
                 readOnly={isReadOnly}
                 value={companyName}
                 onBlur={(e) => onCompanyNameBlur?.(e.target.value)}
                 onChange={(e) => onCompanyNameChange(e.target.value)}
                 isInvalid={!!validationErrors.companyName}
               />
               <Form.Control.Feedback type="invalid" id="companyNameError" aria-live="polite">
                 {validationErrors.companyName}
               </Form.Control.Feedback>
             </Form.Group>
           </Col>
         )}
         <Col md={6}>
           <Form.Group controlId="contactBilling">
             <Form.Label className='visually-hidden' htmlFor="contactBilling">{t('PHONE_NUMBER')}</Form.Label>
             <Form.Control
               id="contactBilling"
               required
               aria-required="true"
               aria-invalid={!!validationErrors.contactBilling}
               aria-describedby={validationErrors.contactBilling ? "contactBillingError" : undefined}
               placeholder={t('PHONE_NUMBER_REQUIRED')}
               type="text"
               autoComplete="none"
               className={`form-control ${isReadOnly ? "disabled" : ""}`}
               name="contactBilling"
               readOnly={isReadOnly}
               value={formatPhoneNo(formBillingDetails.contactBilling)}
               onBlur={(e) => onInputBlur('contactBilling', e.target.value)}
               onChange={(e) => handleFieldChange('contactBilling', e.target.value)}
               isInvalid={!!validationErrors.contactBilling}
             />
             <Form.Control.Feedback type="invalid" id="contactBillingError" aria-live="polite">
               {validationErrors.contactBilling}
             </Form.Control.Feedback>
           </Form.Group>
         </Col>
         {userType !== 'loggedIn' && (
           <Col md={6}>
             <Form.Group controlId="emailBilling">
               <Form.Label className='visually-hidden' htmlFor="emailBilling">{t('EMAIL_ADDRESS_COLON')}</Form.Label>
               <Form.Control
                 id="emailBilling"
                 required
                 aria-required="true"
                 aria-invalid={!!validationErrors.emailBilling}
                 aria-describedby={validationErrors.emailBilling ? "emailBillingError" : undefined}
                 placeholder={t('EMAIL_ADDRESS_REQUIRED')}
                 type="email"
                 autoComplete="none"
                 className={`form-control ${isReadOnly ? "disabled" : ""}`}
                 name="emailBilling"
                 readOnly={isReadOnly}
                 value={formBillingDetails.emailBilling}
                 onBlur={(e) => onInputBlur('emailBilling', e.target.value)}
                 onChange={(e) => handleFieldChange('emailBilling', e.target.value)}
                 isInvalid={!!validationErrors.emailBilling}
               />
               <Form.Control.Feedback type="invalid" id="emailBillingError" aria-live="polite">
                 {validationErrors.emailBilling}
               </Form.Control.Feedback>
             </Form.Group>
           </Col>
         )}
         {userType !== 'loggedIn' && (
           <Col md={6}>
             <Form.Group controlId="vemailBilling">
               <Form.Label className='visually-hidden' htmlFor="vemailBilling">{t('VERIFY_EMAIL_ADDRESS_COLON')}</Form.Label>
               <Form.Control
                 id="vemailBilling"
                 required
                 aria-required="true"
                 aria-invalid={!!validationErrors.vemailBilling}
                 aria-describedby={validationErrors.vemailBilling ? "vemailBillingError" : undefined}
                 placeholder={t('VERIFY_EMAIL_ADDRESS_REQUIRED')}
                 type="email"
                 autoComplete="none"
                 className={`form-control ${isReadOnly ? "disabled" : ""}`}
                 name="vemailBilling"
                 readOnly={isReadOnly}
                 value={formBillingDetails.vemailBilling}
                 onBlur={(e) => onInputBlur('vemailBilling', e.target.value)}
                 onChange={(e) => handleFieldChange('vemailBilling', e.target.value)}
                 isInvalid={!!validationErrors.vemailBilling}
               />
               <Form.Control.Feedback type="invalid" id="vemailBillingError" aria-live="polite">
                 {validationErrors.vemailBilling}
               </Form.Control.Feedback>
             </Form.Group>
           </Col>
        )}
         </Row>
       </div>
    )
}
export default Billing;